import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Divider, Typography, Chip, Drawer, List, ListItem, ListItemText, ListItemButton, Alert } from '@mui/material';
import { Send, Clear, Mic, MicOff, History, Close } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import MessageBubble from '../molecules/MessageBubble';
import TaskStatus, { TaskData } from '../molecules/TaskStatus';
import Text from '../atoms/Text';
import { sendAgentChatMessage, clearSessionId, getOrCreateSessionId, sendAgentMessage } from '../../services/agentChatService';
import COLORS from '../../config/colors';

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'agent';
  timestamp: Date;
  tasks?: TaskData[];
  isHtml?: boolean;
  isTemporary?: boolean;
}

export interface ChatWindowProps {
  onMessageSend?: (message: string) => void;
  onTaskUpdate?: (tasks: TaskData[]) => void;
  onContentUpdate?: (htmlContent: string) => void;
  sx?: any;
  candidate?: any;
  candidateId?: string;
}

const ChatWindow = ({
  onMessageSend,
  onTaskUpdate,
  onContentUpdate,
  sx,
  candidate,
  candidateId,
}: ChatWindowProps) => {
  const { user } = useAuth();
  
  // Check if the user should use the agent chat API (exclude panelist)
  const shouldUseAgentAPI = user?.role && ['admin', 'hr', 'sme'].includes(user.role.toLowerCase());
  
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      content: `Hello ${user?.firstName || 'there'}! I'm your AI agent. I can help you with complex tasks by breaking them down into manageable steps. What would you like me to help you with today?`,
      sender: 'agent',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([
    'Analyze this candidate\'s resume',
    'Generate interview questions',
    'Schedule an interview',
    'Compare with job description',
    'View screening reports',
  ]);
  const [chatHistory, setChatHistory] = useState<Array<{id: string, title: string, date: Date, messages: ChatMessage[]}>>([
    {
      id: '1',
      title: 'Previous Chat - Data Analysis',
      date: new Date(Date.now() - 86400000), // Yesterday
      messages: [
        {
          id: 'h1',
          content: 'Can you help me analyze the sales data?',
          sender: 'user',
          timestamp: new Date(Date.now() - 86400000),
        },
        {
          id: 'h2',
          content: 'I\'ll help you analyze the sales data. Let me break this down into steps.',
          sender: 'agent',
          timestamp: new Date(Date.now() - 86400000),
        }
      ]
    },
    {
      id: '2',
      title: 'UI Design Discussion',
      date: new Date(Date.now() - 172800000), // 2 days ago
      messages: [
        {
          id: 'h3',
          content: 'I need help designing a new dashboard',
          sender: 'user',
          timestamp: new Date(Date.now() - 172800000),
        }
      ]
    }
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Initialize session ID on component mount
  useEffect(() => {
    if (shouldUseAgentAPI) {
      const id = getOrCreateSessionId();
      setSessionId(id);
      console.log('[ChatWindow] Session ID initialized:', id);
    }
  }, [shouldUseAgentAPI]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Update suggestions based on conversation context
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.sender === 'agent' && messages.length > 1) {
      // Update suggestions based on context
      const content = lastMessage.content.toLowerCase();
      
      if (content.includes('resume') || content.includes('candidate')) {
        setSuggestedPrompts([
          'Show candidate strengths',
          'What are the gaps?',
          'Generate interview questions',
          'Compare with job requirements',
        ]);
      } else if (content.includes('interview') || content.includes('questions')) {
        setSuggestedPrompts([
          'Schedule the interview',
          'Add more technical questions',
          'View screening feedback',
          'Compare with other candidates',
        ]);
      } else if (content.includes('schedule') || content.includes('meeting')) {
        setSuggestedPrompts([
          'Send invitation email',
          'View panel availability',
          'Reschedule interview',
          'Add interview notes',
        ]);
      } else {
        // Default suggestions remain as initialized
      }
    }
  }, [messages]);

  const parseQueryIntoTasks = (query: string): TaskData[] => {
    // Simulate AI parsing of query into discrete tasks
    const taskTemplates = [
      {
        keywords: ['analyze', 'data', 'report'],
        tasks: [
          { title: 'Load data source', description: 'Connecting to data repository' },
          { title: 'Parse data structure', description: 'Analyzing data format and schema' },
          { title: 'Generate analysis', description: 'Processing data with statistical methods' },
          { title: 'Create report', description: 'Formatting results into readable report' },
        ],
      },
      {
        keywords: ['create', 'design', 'ui'],
        tasks: [
          { title: 'Understand requirements', description: 'Analyzing design specifications' },
          { title: 'Create wireframe', description: 'Designing component structure' },
          { title: 'Implement components', description: 'Building UI components' },
          { title: 'Test interface', description: 'Validating functionality and accessibility' },
        ],
      },
      {
        keywords: ['search', 'find', 'research'],
        tasks: [
          { title: 'Define search criteria', description: 'Setting up search parameters' },
          { title: 'Query databases', description: 'Searching multiple data sources' },
          { title: 'Filter results', description: 'Applying relevance filters' },
          { title: 'Compile findings', description: 'Organizing results into summary' },
        ],
      },
    ];

    // Find matching template based on keywords
    const template = taskTemplates.find(t => 
      t.keywords.some(keyword => 
        query.toLowerCase().includes(keyword.toLowerCase())
      )
    );

    if (template) {
      return template.tasks.map((task, index) => ({
        id: `task-${Date.now()}-${index}`,
        title: task.title,
        description: task.description,
        status: 'pending' as const,
        startTime: undefined,
        endTime: undefined,
      }));
    }

    // Default generic tasks
    return [
      {
        id: `task-${Date.now()}-1`,
        title: 'Parse request',
        description: 'Understanding your request',
        status: 'pending' as const,
      },
      {
        id: `task-${Date.now()}-2`,
        title: 'Process information',
        description: 'Gathering relevant information',
        status: 'pending' as const,
      },
      {
        id: `task-${Date.now()}-3`,
        title: 'Generate response',
        description: 'Formulating helpful response',
        status: 'pending' as const,
      },
    ];
  };

  const simulateTaskExecution = async (tasks: TaskData[]) => {
    setIsProcessing(true);
    
    for (let i = 0; i < tasks.length; i++) {
      const currentTask = tasks[i];
      
      // Start task
      currentTask.status = 'running';
      currentTask.startTime = new Date();
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage.sender === 'agent' && lastMessage.tasks) {
          return prev.map((msg, index) => 
            index === prev.length - 1 
              ? { ...msg, tasks: [...tasks] }
              : msg
          );
        }
        return prev;
      });

      // Simulate processing time
      const processingTime = 1000 + Math.random() * 2000;
      
      // Simulate progress updates
      for (let progress = 0; progress <= 100; progress += 20) {
        currentTask.progress = progress;
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage.sender === 'agent' && lastMessage.tasks) {
            return prev.map((msg, index) => 
              index === prev.length - 1 
                ? { ...msg, tasks: [...tasks] }
                : msg
            );
          }
          return prev;
        });
        await new Promise(resolve => setTimeout(resolve, processingTime / 5));
      }

      // Complete task
      currentTask.status = 'completed';
      currentTask.endTime = new Date();
      currentTask.result = `Task "${currentTask.title}" completed successfully`;
      currentTask.progress = undefined;
      
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage.sender === 'agent' && lastMessage.tasks) {
          return prev.map((msg, index) => 
            index === prev.length - 1 
              ? { ...msg, tasks: [...tasks] }
              : msg
          );
        }
        return prev;
      });

      // Small delay between tasks
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsProcessing(false);
    onTaskUpdate?.(tasks);
  };

  const generateAgentResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();
    
    // Contextual responses based on query content
    if (lowerQuery.includes('resume') || lowerQuery.includes('candidate')) {
      return `I'm analyzing the candidate's resume now. Let me pull up the details and highlight the key information for you.`;
    } else if (lowerQuery.includes('interview') && lowerQuery.includes('question')) {
      return `Great! I'll generate relevant interview questions based on the job requirements and candidate's background.`;
    } else if (lowerQuery.includes('schedule')) {
      return `I'll help you schedule the interview. Let me check the availability and set up the meeting details.`;
    } else if (lowerQuery.includes('compare') || lowerQuery.includes('job description')) {
      return `I'm comparing the candidate's profile with the job description requirements. I'll highlight the matches and gaps.`;
    } else if (lowerQuery.includes('report') || lowerQuery.includes('screening')) {
      return `Let me retrieve the screening reports and analysis for you.`;
    } else if (lowerQuery.includes('strength') || lowerQuery.includes('skill')) {
      return `I'll analyze the candidate's key strengths and skills from their profile.`;
    } else if (lowerQuery.includes('gap') || lowerQuery.includes('weakness')) {
      return `I'll identify the gaps and areas where the candidate may need development.`;
    } else if (lowerQuery.includes('feedback')) {
      return `Pulling up the feedback and evaluation details now.`;
    } else {
      return `I understand! Let me process that request and show you the relevant information.`;
    }
  };

  // Helper function to format response text into HTML
  const formatResponseToHtml = (text: string): string => {
    // Replace newlines with <br /> for basic formatting
    let formatted = text
      .split('\n\n')
      .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br />')}</p>`)
      .join('');
    
    // Handle bullet points (lines starting with - or *)
    formatted = formatted.replace(/<p>([•\-*])\s+(.+?)<\/p>/g, '<li>$2</li>');
    
    // Wrap consecutive <li> elements in <ul>
    formatted = formatted.replace(/(<li>.*?<\/li>)+/gs, match => `<ul>${match}</ul>`);
    
    // Handle bold text (**text** or __text__)
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.+?)__/g, '<strong>$1</strong>');
    
    // Handle italic text (*text* or _text_)
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.+?)_/g, '<em>$1</em>');
    
    return formatted;
  };

  // Helper function to process agent API response
  const processAgentResponse = async (responseText: string) => {
    // Split the response by double newline to separate sections
    const lines = responseText.split('\n\n');
    
    if (lines.length > 1) {
      // First line/section is the temporary "intent" message
      const firstLine = lines[0].trim();
      
      // Rest of the content
      const restContent = lines.slice(1).join('\n\n').trim();
      
      // Show temporary message first
      const tempMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        content: firstLine,
        sender: 'agent',
        timestamp: new Date(),
        isTemporary: true,
      };
      
      setMessages(prev => [...prev, tempMessage]);
      
      // Wait for the temporary message to be visible (3 seconds as per animation)
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Remove temporary message and add formatted response
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      
      // Format the rest of the content as HTML
      const formattedContent = formatResponseToHtml(restContent);
      
      const finalMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: formattedContent,
        sender: 'agent',
        timestamp: new Date(),
        isHtml: true,
      };
      
      setMessages(prev => [...prev, finalMessage]);
    } else {
      // If there's only one section, just show it normally
      const formattedContent = formatResponseToHtml(responseText);
      
      const message: ChatMessage = {
        id: Date.now().toString(),
        content: formattedContent,
        sender: 'agent',
        timestamp: new Date(),
        isHtml: true,
      };
      
      setMessages(prev => [...prev, message]);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    
    const query = inputValue.trim();
    setInputValue('');
    
    // Call parent callback to update right pane
    onMessageSend?.(query);

    setIsProcessing(true);
    setApiError(null);

    // If the user should use the agent API, call it
    if (shouldUseAgentAPI) {
      try {
        console.log('[ChatWindow] Sending message to both agent APIs (decoupled):', query);

        // Start both calls in parallel
        const chatPromise = sendAgentChatMessage(
          query,
          user?.role || 'user',
          [] // History is left blank for now as per requirements
        );
        const messagePromise = sendAgentMessage(query, sessionId);

        // Handle chat response first (left pane)
        try {
          const chatResponse = await chatPromise;
          console.log('[ChatWindow] Received chat response:', chatResponse);
          const agentContent = chatResponse.response_text || chatResponse.response || chatResponse.content || 'I received your message but couldn\'t generate a response.';
          await processAgentResponse(agentContent);
        } catch (chatError: any) {
          console.error('[ChatWindow] Chat API error:', chatError);
          setApiError(chatError.message || 'Chat service failed. Showing fallback response.');
          await new Promise(resolve => setTimeout(resolve, 800));
          const agentResponse = generateAgentResponse(query);
          const agentMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            content: agentResponse,
            sender: 'agent',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, agentMessage]);
        }

        // Handle message response (right pane) independently
        messagePromise
          .then((messageResponse) => {
            console.log('[ChatWindow] Received message response:', messageResponse);
            if (messageResponse.response_text && onContentUpdate) {
              onContentUpdate(messageResponse.response_text);
            }
          })
          .catch((messageError: any) => {
            console.error('[ChatWindow] Message API error:', messageError);
            // Ensure right pane stops loading even on error
            if (onContentUpdate) {
              onContentUpdate('<div style="padding:12px;color:#991b1b;">Failed to load assistant content.</div>');
            }
          });
      } catch (error: any) {
        console.error('[ChatWindow] Error initiating agent API calls:', error);
        setApiError(error.message || 'Failed to communicate with the agent. Using fallback response.');
        await new Promise(resolve => setTimeout(resolve, 800));
        const agentResponse = generateAgentResponse(query);
        const agentMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: agentResponse,
          sender: 'agent',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, agentMessage]);
      }
    } else {
      // For panelist role, use the mock response
      await new Promise(resolve => setTimeout(resolve, 800));
      const agentResponse = generateAgentResponse(query);
      const agentMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: agentResponse,
        sender: 'agent',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, agentMessage]);
    }

    setIsProcessing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: '1',
        content: `Chat cleared! How can I help you today?`,
        sender: 'agent',
        timestamp: new Date(),
      },
    ]);
    
    // Clear session ID to start a new conversation
    if (shouldUseAgentAPI) {
      clearSessionId();
      const newSessionId = getOrCreateSessionId();
      setSessionId(newSessionId);
      console.log('[ChatWindow] New session ID created:', newSessionId);
    }
    
    // Reset suggestions to default
    setSuggestedPrompts([
      'Analyze this candidate\'s resume',
      'Generate interview questions',
      'Schedule an interview',
      'Compare with job description',
      'View screening reports',
    ]);
    
    // Clear any errors
    setApiError(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        processAudioInput(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudioInput = async (audioBlob: Blob) => {
    // Simulate speech-to-text processing
    setIsListening(true);
    
    // In a real implementation, you would send the audioBlob to a speech-to-text service
    // For demo purposes, we'll simulate this with a delay and mock text
    setTimeout(() => {
      const mockTranscription = "This is a simulated transcription of your voice input. In a real implementation, this would be the actual speech-to-text result.";
      setInputValue(mockTranscription);
      setIsListening(false);
      
      // Auto-send the transcribed message
      setTimeout(() => {
        if (mockTranscription.trim()) {
          const userMessage: ChatMessage = {
            id: Date.now().toString(),
            content: mockTranscription,
            sender: 'user',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, userMessage]);
          setInputValue('');
          
          // Process the message
          const tasks = parseQueryIntoTasks(mockTranscription);
          const agentMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            content: `I heard you say: "${mockTranscription}". I've broken down your request into ${tasks.length} tasks. Let me process them step by step:`,
            sender: 'agent',
            timestamp: new Date(),
            tasks,
          };
          setMessages(prev => [...prev, agentMessage]);
          simulateTaskExecution(tasks);
        }
      }, 500);
    }, 2000);
  };

  const toggleAudioMode = () => {
    setIsAudioMode(!isAudioMode);
    if (isRecording) {
      stopRecording();
    }
  };

  const loadChatHistory = (historyItem: any) => {
    setMessages(historyItem.messages);
    setShowHistory(false);
  };

  const saveChatHistory = () => {
    if (messages.length > 1) {
      const newHistory = {
        id: Date.now().toString(),
        title: `Chat - ${new Date().toLocaleDateString()}`,
        date: new Date(),
        messages: [...messages]
      };
      setChatHistory(prev => [newHistory, ...prev]);
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (isProcessing) return;
    
    // Set the input value first for visual feedback
    setInputValue(suggestion);
    
    // Small delay to show the suggestion in the input
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Create and send the message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: suggestion,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    
    // Call parent callback to update right pane
    onMessageSend?.(suggestion);

    setIsProcessing(true);
    setApiError(null);

    // If the user should use the agent API, call it
    if (shouldUseAgentAPI) {
      try {
        console.log('[ChatWindow] Sending suggestion to both agent APIs in parallel:', suggestion);
        
        // Call both APIs in parallel with the original user message
        const [chatResponse, messageResponse] = await Promise.all([
          sendAgentChatMessage(
            suggestion,
            user?.role || 'user',
            [] // History is left blank for now as per requirements
          ),
          sendAgentMessage(suggestion, sessionId)
        ]);
        
        console.log('[ChatWindow] Received chat response:', chatResponse);
        console.log('[ChatWindow] Received message response:', messageResponse);
        
        // Extract the response content from chat API
        const agentContent = chatResponse.response_text || chatResponse.response || chatResponse.content || 'I received your message but couldn\'t generate a response.';
        
        // Process the agent response with formatting for chat window
        await processAgentResponse(agentContent);
        
        // Pass the HTML content to the parent component to display in the right panel
        if (messageResponse.response_text && onContentUpdate) {
          onContentUpdate(messageResponse.response_text);
        }
      } catch (error: any) {
        console.error('[ChatWindow] Error calling agent APIs:', error);
        setApiError(error.message || 'Failed to communicate with the agent. Using fallback response.');
        
        // Fallback to mock response if API fails
        await new Promise(resolve => setTimeout(resolve, 800));
        const agentResponse = generateAgentResponse(suggestion);
        const agentMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: agentResponse,
          sender: 'agent',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, agentMessage]);
      }
    } else {
      // For panelist role, use the mock response
      await new Promise(resolve => setTimeout(resolve, 800));
      const agentResponse = generateAgentResponse(suggestion);
      const agentMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: agentResponse,
        sender: 'agent',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, agentMessage]);
    }

    setIsProcessing(false);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.3)',
          background: 'rgba(255,255,255,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: 'var(--font-heading)',
                fontWeight: 700, 
                color: '#2c3e50', 
                textShadow: 'none',
                letterSpacing: 0.1
              }}
            >
              💬 Chat Assistant
            </Typography>
            {shouldUseAgentAPI && (
              <Chip
                label="🤖 AI Powered"
                sx={{ 
                  borderRadius: 2, 
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  fontSize: 11,
                  background: '#10b981',
                  color: '#fff',
                  border: 'none',
                  height: 22,
                }}
                size="small"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={isAudioMode ? "🎤 Voice" : "⌨️ Text"}
              sx={{ 
                borderRadius: 2, 
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
                fontSize: 13,
                background: 'var(--primary-blue)',
                color: '#fff',
                border: 'none'
              }}
              size="small"
            />
            <IconButton 
              size="small" 
              onClick={toggleAudioMode}
              sx={{ 
                bgcolor: 'rgba(0,0,0,0.1)',
                color: '#2c3e50',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.2)',
                }
              }}
            >
              {isAudioMode ? <Mic fontSize="small" /> : <MicOff fontSize="small" />}
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => setShowHistory(true)}
              sx={{ 
                bgcolor: 'rgba(0,0,0,0.1)',
                color: '#2c3e50',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.2)',
                }
              }}
            >
              <History fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleClearChat}
              sx={{ 
                bgcolor: 'rgba(0,0,0,0.1)',
                color: '#2c3e50',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.2)',
                }
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
      </Box>

      {/* API Error Alert */}
      {apiError && (
        <Alert 
          severity="warning" 
          onClose={() => setApiError(null)}
          sx={{ 
            m: 2, 
            borderRadius: 2,
            fontFamily: 'var(--font-body)',
          }}
        >
          {apiError}
        </Alert>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          background: 'var(--light-gray)',
          minHeight: 0, // Important: allows flex child to shrink below content size
        }}
      >
        {messages.map((message) => (
          <Box key={message.id}>
            <MessageBubble
              content={message.content}
              sender={message.sender}
              timestamp={message.timestamp}
              userName={message.sender === 'agent' ? 'AI Agent' : user?.firstName}
              isHtml={message.isHtml}
              isTemporary={message.isTemporary}
            />
            
            {/* Show tasks for agent messages */}
            {message.tasks && message.tasks.length > 0 && (
              <Box sx={{ ml: 5, mb: 2 }}>
                {message.tasks.map((task) => (
                  <TaskStatus
                    key={task.id}
                    task={task}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ))}
        
        {/* Show typing indicator when processing */}
        {isProcessing && (
          <Box sx={{ ml: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                bgcolor: COLORS.INFO_LIGHT,
                borderRadius: 3,
                px: 2.5,
                py: 1.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'var(--primary-blue)',
                  animation: 'pulse 1.4s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 1 },
                  },
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'var(--primary-blue)',
                  animation: 'pulse 1.4s ease-in-out 0.2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 1 },
                  },
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'var(--primary-blue)',
                  animation: 'pulse 1.4s ease-in-out 0.4s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 1 },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  ml: 1,
                  color: 'var(--primary-blue)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 500,
                }}
              >
                AI is thinking...
              </Typography>
            </Box>
          </Box>
        )}
        
        <Box ref={messagesEndRef} />
      </Box>

      {/* Suggestion Tiles */}
      {suggestedPrompts.length > 0 && !isProcessing && !isListening && (
        <Box
          sx={{
            p: 2,
            background: 'var(--light-gray)',
            borderTop: '1px solid #e0e7ff',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1.5,
              color: 'var(--dark-gray)',
              fontFamily: 'var(--font-body)',
              fontWeight: 600,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            💡 Suggested Actions
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {suggestedPrompts.map((prompt, index) => (
              <Chip
                key={index}
                label={prompt}
                onClick={() => handleSuggestionClick(prompt)}
                sx={{
                  borderRadius: 2,
                  bgcolor: 'var(--white)',
                  color: 'var(--primary-blue)',
                  border: '1px solid #e0e7ff',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'var(--primary-blue)',
                    color: 'var(--white)',
                    borderColor: 'var(--primary-blue)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider />

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          background: 'rgba(255,255,255,0.1)',
          borderTop: '1px solid rgba(255,255,255,0.3)',
        }}
      >
        {isListening && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Chip
              label="🎤 Processing voice input..."
              sx={{ 
                borderRadius: 2, 
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
                fontSize: 13,
                background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                color: '#fff'
              }}
            />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isAudioMode ? "🎤 Voice mode active - click mic to record" : "💬 Ask me to help with any task..."}
            variant="outlined"
            size="small"
            disabled={isProcessing || isListening}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(255,255,255,0.5)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.95)',
                },
                '&.Mui-focused': {
                  bgcolor: '#fff',
                }
              },
            }}
          />
          
          {isAudioMode && (
            <IconButton
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing || isListening}
              sx={{
                background: isRecording 
                  ? '#dc3545' 
                  : 'var(--primary-blue)',
                color: 'white',
                borderRadius: 3,
                p: 1.5,
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  bgcolor: '#e9ecef',
                  color: '#6c757d',
                },
              }}
            >
              {isRecording ? <MicOff /> : <Mic />}
            </IconButton>
          )}
          
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isProcessing || isListening}
            sx={{
              background: 'var(--primary-blue)',
              color: 'white',
              borderRadius: 3,
              p: 1.5,
              '&:hover': {
                transform: 'scale(1.05)',
                background: 'var(--primary-blue-dark)',
              },
              '&:disabled': {
                bgcolor: '#e9ecef',
                color: '#6c757d',
              },
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>

      {/* Chat History Drawer */}
      <Drawer
        anchor="left"
        open={showHistory}
        onClose={() => setShowHistory(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 350,
            background: 'var(--white)',
            color: 'var(--dark-gray)',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e7ff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                letterSpacing: 0.1
              }}
            >
              📚 Chat History
            </Typography>
            <IconButton 
              onClick={() => setShowHistory(false)}
              sx={{ color: '#2c3e50' }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
        
        <List sx={{ flex: 1, p: 1 }}>
          {chatHistory.map((historyItem) => (
            <ListItem key={historyItem.id} sx={{ p: 0, mb: 1 }}>
              <ListItemButton
                onClick={() => loadChatHistory(historyItem)}
                sx={{
                  borderRadius: 2,
                  background: 'var(--light-gray)',
                  '&:hover': {
                    background: '#e9ecef',
                  },
                  p: 2,
                }}
              >
                <ListItemText
                  primary={historyItem.title}
                  secondary={historyItem.date.toLocaleDateString()}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: 'var(--dark-gray)',
                      fontFamily: 'var(--font-body)',
                      fontWeight: 600,
                      fontSize: 14,
                    },
                    '& .MuiListItemText-secondary': {
                      color: 'var(--primary-blue)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Box>
  );
};

export default ChatWindow;